<script>
import { apiSecond } from '@/boot/axios'
import { loadFile } from '@/helpers'

import Region from '@/components/components/Inputs/region'
import AqEmpty from '@/components/aqua/AqEmpty.vue'
import AqKpi from '@/components/aqua/AqKpi.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AqStatus from '@/components/aqua/AqStatus.vue'
import AquaSelect from './AquaSelect.vue'

const FORMS = [1, 2, 3, 4, 5, 6]
const LEGEND = ['tir_signed', 'subject_signed', 'draft', 'canceled_by_tir', 'missing']

export default {
	name: 'AquaCompliance',
	components: {
		Region,
		AqEmpty,
		AqKpi,
		AqPageHeader,
		AqStatus,
		AquaSelect,
	},
	props: {
		scope: {
			type: String,
			default: 'go',
		},
	},
	data() {
		return {
			forms: FORMS,
			legend: LEGEND,
			rows: [],
			summary: null,
			unregistered: [],
			loading: false,
			exporting: false,
			total: 0,
			options: { page: 1, itemsPerPage: 20 },
			filters: {
				report_year: new Date().getFullYear() - 1,
				region_id: null,
				subject_kind: '',
				search: '',
				only_incomplete: false,
			},
		}
	},
	computed: {
		years() {
			const current = new Date().getFullYear()
			return Array.from({ length: 7 }, (_, index) => current - index)
		},
		yearOptions() {
			return this.years.map((year) => ({ value: year, text: year }))
		},
		kindOptions() {
			return [
				{ value: '', text: this.$t('aqua.ui.compliance.all_kinds') },
				{ value: 'aquaculture', text: this.$t('subject_kind.aquaculture') },
				{ value: 'feed_producer', text: this.$t('subject_kind.feed_producer') },
			]
		},
		headers() {
			return [
				{ text: this.$t('aqua.subject'), value: 'subject', sortable: false, width: '250px' },
				...this.forms.map((form) => ({ text: `${form}-аква`, value: `form_${form}`, sortable: false, align: 'center' })),
				{ text: this.$t('aqua.compliance.required'), value: 'completeness', sortable: false, align: 'center', width: '150px' },
			]
		},
		apiBase() {
			return `v1/aqua/${this.scope}/compliance`
		},
		reportBase() {
			return this.scope === 'go' ? 'reporting-analytics/aqua' : 'reporting/aqua-tir'
		},
		backPath() {
			return `/${this.$i18n.locale}/account/${this.scope === 'go' ? 'reporting-analytics' : 'reporting'}`
		},
		hasFilters() {
			const { region_id, subject_kind, search, only_incomplete } = this.filters
			return Boolean(region_id || subject_kind || search.trim() || only_incomplete)
		},
		completePercent() {
			if (!this.summary || !this.summary.subjects) return 0
			return Math.round((this.summary.complete / this.summary.subjects) * 100)
		},
	},
	watch: {
		'filters.report_year'() {
			this.reload()
		},
		'filters.region_id'() {
			this.reload()
		},
		'filters.subject_kind'() {
			this.reload()
		},
		'filters.only_incomplete'() {
			this.reload()
		},
		'filters.search'(value) {
			if (!value.trim()) this.reload()
		},
	},
	methods: {
		params() {
			const params = { report_year: this.filters.report_year }
			if (this.filters.region_id) params.region_id = this.filters.region_id
			if (this.filters.subject_kind) params.subject_kind = this.filters.subject_kind
			if (this.filters.search.trim()) params.search = this.filters.search.trim()
			if (this.filters.only_incomplete) params.only_incomplete = 1
			return params
		},
		reload() {
			this.options = { ...this.options, page: 1 }
			this.fetch(true)
		},
		resetFilters() {
			this.filters = { ...this.filters, region_id: null, subject_kind: '', search: '', only_incomplete: false }
		},
		onOptions(options) {
			const changed = options.page !== this.options.page || options.itemsPerPage !== this.options.itemsPerPage
			this.options = options
			if (changed || !this.summary) this.fetch(!this.summary)
		},
		async fetch(withSummary = false) {
			this.loading = true
			try {
				const params = { ...this.params(), page: this.options.page, per_page: this.options.itemsPerPage }
				if (withSummary) params.with_summary = 1
				const { data } = await apiSecond.get(this.apiBase, { params })
				this.rows = data.data
				if (data.summary) this.summary = data.summary
				if (withSummary) this.unregistered = data.unregistered || []
				this.total = data.meta.total
			} catch (error) {
				this.handleError(error)
			} finally {
				this.loading = false
			}
		},
		async exportExcel() {
			if (this.exporting) return
			this.exporting = true
			try {
				const response = await apiSecond.get(`${this.apiBase}/export`, { params: this.params(), responseType: 'blob' })
				loadFile(response.data, response.headers['content-type'], `aqua-compliance-${this.filters.report_year}`, 'xlsx')
			} catch (error) {
				this.handleError(await this.parseBlobError(error))
			} finally {
				this.exporting = false
			}
		},
		async parseBlobError(error) {
			const data = error?.response?.data
			if (!(data instanceof Blob)) return error
			try {
				error.response.data = JSON.parse(await data.text())
			} catch (parseError) {
				error.response.data = {}
			}
			return error
		},
		cell(row, form) {
			return row.forms[form] || null
		},
		statusText(cell) {
			return cell.status === 'missing' ? this.$t('aqua.compliance.missing') : this.$t(`aqua.status.${cell.status}`)
		},
		shortText(cell) {
			return this.$t(`aqua.ui.compliance.short_${cell.status}`)
		},
		statusTone(cell) {
			if (cell.status === 'missing') return cell.required ? 'danger' : 'neutral'
			return ''
		},
		completenessTone(row) {
			if (row.complete) return 'success'
			return row.submitted_required ? 'warning' : 'danger'
		},
		completenessWidth(row) {
			if (!row.required_total) return '100%'
			return `${Math.round((row.submitted_required / row.required_total) * 100)}%`
		},
		number(value) {
			return new Intl.NumberFormat('ru-RU').format(value || 0)
		},
		reportLink(form, cell) {
			return `/${this.$i18n.locale}/account/${this.reportBase}/${form}/${cell.report_id}`
		},
		handleError(error) {
			if (error?.response?.status === 401) {
				this.$router.push(`/${this.$i18n.locale}/login`)
				return
			}
			this.$toast.error(error?.response?.data?.message || this.$t('system_message.500'))
		},
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
}
</script>

<template>
	<div class="wrap__content aq-page">
		<div class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="[scope === 'go' ? 'ReportingAnalyticManagement' : 'ReportingManagement']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="$t('aqua.compliance.title')"
								:eyebrow="$t('aqua.ui.compliance.eyebrow')"
								:subtitle="$t('aqua.compliance.subtitle')"
								:back="backPath"
								:back-label="$t('aqua.ui.compliance.back')"
							>
								<template #actions>
									<button type="button" class="aq-btn" :disabled="exporting" @click="exportExcel">
										<span v-if="exporting" class="co-spinner" aria-hidden="true"></span>
										<svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
										</svg>
										{{ $t('aqua.ui.compliance.export') }}
									</button>
								</template>
							</AqPageHeader>

							<div v-if="summary" class="co-kpis">
								<AqKpi :label="$t('aqua.compliance.subjects')" :value="number(summary.subjects)" />
								<AqKpi
									tone="success"
									:label="$t('aqua.compliance.complete')"
									:value="number(summary.complete)"
									:progress="completePercent"
									:hint="$t('aqua.ui.compliance.complete_hint', { percent: `${completePercent}%` })"
								/>
								<AqKpi
									:tone="summary.incomplete ? 'danger' : 'success'"
									:label="$t('aqua.compliance.incomplete')"
									:value="number(summary.incomplete)"
									:hint="$t('aqua.ui.compliance.incomplete_hint')"
								/>
								<AqKpi
									:tone="summary.late ? 'warning' : 'neutral'"
									:label="$t('aqua.compliance.late')"
									:value="number(summary.late)"
									:hint="$t('aqua.ui.compliance.late_hint')"
								/>
								<AqKpi
									:tone="summary.tir_late ? 'warning' : 'neutral'"
									:label="$t('aqua.compliance.tir_late')"
									:value="number(summary.tir_late)"
									:hint="$t('aqua.ui.compliance.tir_late_hint')"
								/>
							</div>
							<div v-else-if="loading" class="co-kpis" aria-busy="true">
								<span v-for="index in 5" :key="index" class="co-skeleton"></span>
							</div>

							<details v-if="unregistered.length" class="aq-alert aq-alert--warning co-unregistered">
								<summary class="co-unregistered__summary">
									<strong>{{ $t('aqua.ui.compliance.unregistered_title', { count: unregistered.length }) }}</strong>
									<span>{{ $t('aqua.ui.compliance.unregistered_hint') }}</span>
								</summary>
								<ul class="co-unregistered__list">
									<li v-for="item in unregistered" :key="item.bin">
										{{ item.title }} <span class="co-unregistered__bin">{{ item.bin }}</span>
									</li>
								</ul>
							</details>

							<section class="aq-card co-toolbar">
								<div class="co-filters">
									<AquaSelect v-model="filters.report_year" :label="$t('aqua.ui.compliance.year')" :options="yearOptions" />
									<AquaSelect v-model="filters.subject_kind" :label="$t('aqua.ui.compliance.subject_kind')" :options="kindOptions" />
									<div v-if="scope === 'go'" class="co-filter">
										<span class="co-filter__label">{{ $t('aqua.ui.compliance.region') }}</span>
										<region
											:modelValue="filters.region_id"
											:placeholder="$t('inputs.region')"
											@update:modelValue="filters.region_id = $event"
										></region>
									</div>
									<form class="co-filter co-filter--search" role="search" @submit.prevent="reload">
										<label class="co-filter__label" for="co-search">{{ $t('aqua.ui.compliance.search') }}</label>
										<span class="co-search">
											<svg class="co-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
												<circle cx="11" cy="11" r="7" />
												<path d="M20 20l-3.5-3.5" />
											</svg>
											<input id="co-search" v-model="filters.search" class="aq-input co-search__input" type="search" :placeholder="$t('aqua.compliance.search')" />
											<button type="submit" class="aq-btn aq-btn--primary co-search__btn">{{ $t('aqua.ui.compliance.search_button') }}</button>
										</span>
									</form>
								</div>
								<div class="co-toolbar__row">
									<label class="co-switch">
										<input v-model="filters.only_incomplete" type="checkbox" class="co-switch__input" />
										<span class="co-switch__track" aria-hidden="true"><span class="co-switch__thumb"></span></span>
										{{ $t('aqua.compliance.only_incomplete') }}
									</label>
									<button v-if="hasFilters" type="button" class="aq-btn aq-btn--ghost co-reset" @click="resetFilters">
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
											<path d="M6 6l12 12M18 6L6 18" />
										</svg>
										{{ $t('aqua.ui.compliance.reset') }}
									</button>
								</div>
							</section>

							<section class="aq-card co-table">
								<div class="co-legend">
									<span v-for="status in legend" :key="status" class="co-legend__item">
										<AqStatus :value="status" :tone="status === 'missing' ? 'danger' : ''" :label="shortText({ status })" />
										<span v-if="shortText({ status }) !== statusText({ status })" class="co-legend__note">{{ statusText({ status }) }}</span>
									</span>
									<span class="co-legend__note">{{ $t('aqua.ui.compliance.legend_optional') }}</span>
									<span class="co-legend__note">{{ $t('aqua.ui.compliance.legend_na') }}</span>
								</div>
								<p class="co-hint">{{ $t('aqua.compliance.hint') }}</p>
								<v-data-table
									class="co-dt"
									:headers="headers"
									:items="rows"
									:loading="loading"
									loader-height="3"
									:options="options"
									:server-items-length="total"
									:footer-props="{ 'items-per-page-options': [20, 50, 100] }"
									@update:options="onOptions"
								>
									<template v-slot:item.subject="{ item }">
										<div class="co-subject">
											<strong class="co-subject__name">{{ item.name }}</strong>
											<span class="co-subject__meta">{{ item.iin_bin }} · {{ $t(`subject_kind.${item.subject_kind}`) }}</span>
											<span v-if="item.regions.length" class="co-subject__meta">{{ item.regions.join(', ') }}</span>
										</div>
									</template>
									<template v-for="form in forms" v-slot:[`item.form_${form}`]="{ item }">
										<span v-if="!cell(item, form)" :key="form" class="co-na" :aria-label="$t('aqua.ui.compliance.legend_na')">—</span>
										<div v-else :key="form" class="co-cell" :class="{ 'co-cell--optional': !cell(item, form).required }">
											<router-link
												v-if="cell(item, form).report_id"
												:to="reportLink(form, cell(item, form))"
												class="co-cell__link"
												:title="statusText(cell(item, form))"
											>
												<AqStatus :value="cell(item, form).status" :tone="statusTone(cell(item, form))" :label="shortText(cell(item, form))" />
											</router-link>
											<AqStatus
												v-else
												:value="cell(item, form).status"
												:tone="statusTone(cell(item, form))"
												:label="shortText(cell(item, form))"
												:title="statusText(cell(item, form))"
											/>
											<span v-if="cell(item, form).is_late" class="co-flag">{{ $t('aqua.late') }}</span>
											<span v-if="cell(item, form).report_id && cell(item, form).is_tir_late" class="co-flag co-flag--warning">
												{{ $t('aqua.compliance.tir_late_short') }}
											</span>
										</div>
									</template>
									<template v-slot:item.completeness="{ item }">
										<div class="co-done" :class="`co-done--${completenessTone(item)}`">
											<strong>{{ item.submitted_required }} / {{ item.required_total }}</strong>
											<span class="co-done__track"><span :style="{ width: completenessWidth(item) }"></span></span>
										</div>
									</template>
									<template v-slot:no-data>
										<AqEmpty :title="$t('aqua.ui.compliance.empty_title')" :text="$t('aqua.ui.compliance.empty_text')">
											<button v-if="hasFilters" type="button" class="aq-btn" @click="resetFilters">{{ $t('aqua.ui.compliance.reset') }}</button>
										</AqEmpty>
									</template>
								</v-data-table>
							</section>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.co-kpis {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 20px;
}
.co-unregistered {
	display: block;
}
.co-unregistered__summary {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 10px;
	cursor: pointer;
	list-style-position: inside;
}
.co-unregistered__summary span {
	color: var(--aq-ink-2);
}
.co-unregistered__list {
	margin: 10px 0 0;
	padding-left: 20px;
	columns: 2 280px;
}
.co-unregistered__bin {
	color: var(--aq-muted);
	font-size: 13px;
	font-variant-numeric: tabular-nums;
}
.co-skeleton {
	height: 112px;
	border-radius: var(--aq-radius);
	background: linear-gradient(90deg, #edf2f8 0%, #f6f9fc 50%, #edf2f8 100%);
	background-size: 200% 100%;
	animation: co-shimmer 1.4s ease-in-out infinite;
}
@keyframes co-shimmer {
	from {
		background-position: 200% 0;
	}
	to {
		background-position: -200% 0;
	}
}
.co-spinner {
	width: 16px;
	height: 16px;
	border: 2px solid currentColor;
	border-right-color: transparent;
	border-radius: 50%;
	animation: co-spin 0.8s linear infinite;
}
@keyframes co-spin {
	to {
		transform: rotate(360deg);
	}
}
.co-toolbar {
	padding: 18px 24px 14px;
}
.co-filters {
	display: grid;
	grid-template-columns: 140px minmax(170px, 1fr) minmax(200px, 1fr) minmax(280px, 1.4fr);
	align-items: end;
	gap: 14px 16px;
}
.co-filter--search {
	grid-column: -2 / -1;
}
.co-filter {
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
	margin: 0;
}
.co-filter__label,
.co-filters ::v-deep .aqua-select__label {
	margin: 0;
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.co-filters ::v-deep .aqua-select {
	width: 100%;
	gap: 6px;
}
.co-filters ::v-deep .aqua-select__control {
	height: 44px;
	border-color: var(--aq-border-strong);
	border-radius: 10px;
	transition: border-color 0.2s var(--aq-ease);
}
.co-filters ::v-deep .aqua-select__control:hover {
	border-color: var(--aq-primary);
}
.co-filters ::v-deep .input-wrapper {
	margin: 0;
}
.co-filters ::v-deep .multiselect,
.co-filters ::v-deep .multiselect__tags {
	min-height: 44px;
}
.co-filters ::v-deep .multiselect__tags {
	height: 44px;
}
.co-filters ::v-deep .multiselect__placeholder,
.co-filters ::v-deep .multiselect__single {
	margin-bottom: 0;
	padding-top: 0;
}
.co-filters ::v-deep .input-body .multiselect__tags {
	padding-top: 11px;
	padding-left: 14px;
	border: 1px solid var(--aq-border-strong) !important;
	border-radius: 10px;
}
.co-filters ::v-deep .input-body .multiselect__tags:hover {
	border-color: var(--aq-primary) !important;
}
.co-filters ::v-deep .multiselect__select {
	height: 42px;
}
.co-filters ::v-deep .multiselect__single,
.co-filters ::v-deep .multiselect__placeholder {
	overflow: hidden;
	font-size: 15px;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.co-search {
	position: relative;
	display: flex;
	gap: 8px;
}
.co-search__icon {
	position: absolute;
	top: 13px;
	left: 12px;
	color: var(--aq-muted);
	pointer-events: none;
}
.co-search__input {
	flex: 1 1 auto;
	min-width: 0;
	padding-left: 38px;
}
.co-search__btn {
	flex-shrink: 0;
}
.co-toolbar__row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 14px;
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
}
.co-switch {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	min-height: 44px;
	margin: 0;
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 500;
	cursor: pointer;
}
.co-switch__input {
	position: absolute;
	opacity: 0;
	pointer-events: none;
}
.co-switch__track {
	position: relative;
	flex-shrink: 0;
	width: 40px;
	height: 24px;
	border-radius: 999px;
	background: var(--aq-border-strong);
	transition: background 0.2s var(--aq-ease);
}
.co-switch__thumb {
	position: absolute;
	top: 3px;
	left: 3px;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: #fff;
	box-shadow: 0 1px 3px rgba(21, 29, 72, 0.2);
	transition: transform 0.2s var(--aq-ease);
}
.co-switch__input:checked + .co-switch__track {
	background: var(--aq-primary);
}
.co-switch__input:checked + .co-switch__track .co-switch__thumb {
	transform: translateX(16px);
}
.co-switch__input:focus-visible + .co-switch__track {
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.co-reset {
	min-height: 40px;
}
.co-table {
	overflow: hidden;
}
.co-legend {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 10px;
	padding: 16px 24px 0;
}
.co-legend__item {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-right: 8px;
}
.co-legend__note {
	color: var(--aq-muted);
	font-size: 13px;
}
.co-kpis ::v-deep .aq-kpi__label {
	min-height: 2.8em;
	line-height: 1.4;
}
.co-hint {
	margin: 10px 0 0;
	padding: 0 24px 14px;
	border-bottom: 1px solid var(--aq-border);
	color: var(--aq-muted);
	font-size: 13px;
	line-height: 1.5;
}
.co-subject {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 4px 0;
}
.co-subject__name {
	color: var(--aq-ink);
	font-size: 14px;
	font-weight: 600;
}
.co-subject__meta {
	color: var(--aq-muted);
	font-size: 12px;
	line-height: 1.4;
}
.co-cell {
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
}
.co-cell .aq-badge {
	padding: 3px 8px;
	font-size: 12px;
}
.co-cell--optional {
	opacity: 0.55;
}
.co-cell__link {
	border-radius: 999px;
	text-decoration: none;
	transition: box-shadow 0.2s var(--aq-ease);
}
.co-cell__link:hover {
	box-shadow: 0 0 0 2px var(--aq-border-strong);
}
.co-cell__link:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.35);
}
.co-flag {
	color: var(--aq-danger);
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
}
.co-flag--warning {
	color: var(--aq-warning);
}
.co-na {
	color: #b6c3d3;
}
.co-done {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
}
.co-done__track {
	width: 72px;
	height: 6px;
	border-radius: 999px;
	background: #edf2f8;
	overflow: hidden;
}
.co-done__track span {
	display: block;
	height: 100%;
	border-radius: inherit;
	transition: width 0.3s var(--aq-ease);
}
.co-done--success {
	color: var(--aq-success);
}
.co-done--success .co-done__track span {
	background: var(--aq-success);
}
.co-done--warning {
	color: var(--aq-warning);
}
.co-done--warning .co-done__track span {
	background: #e39a1b;
}
.co-done--danger {
	color: var(--aq-danger);
}
.co-done--danger .co-done__track span {
	background: var(--aq-danger);
}
.co-dt ::v-deep table {
	font-variant-numeric: tabular-nums;
}
.co-dt ::v-deep thead th {
	height: 48px;
	background: #f8fafd;
	color: var(--aq-ink-2) !important;
	font-size: 12px !important;
	padding: 8px !important;
	font-weight: 600;
	line-height: 1.3;
}
.co-dt ::v-deep .text-center {
	text-align: center !important;
}
.co-dt ::v-deep tbody td {
	height: 64px;
	padding: 8px !important;
	border-bottom: 1px solid var(--aq-border) !important;
	color: var(--aq-ink);
}
.co-dt ::v-deep thead th:first-child,
.co-dt ::v-deep tbody td:first-child {
	position: sticky;
	left: 0;
	z-index: 1;
	padding-left: 24px !important;
	box-shadow: inset -1px 0 0 var(--aq-border);
}
.co-dt ::v-deep thead th:first-child {
	background: #f8fafd;
}
.co-dt ::v-deep tbody td:first-child {
	background: var(--aq-surface);
}
.co-dt ::v-deep tbody tr {
	transition: background 0.15s var(--aq-ease);
}
.co-dt ::v-deep tbody tr:hover:not(.v-data-table__empty-wrapper),
.co-dt ::v-deep tbody tr:hover:not(.v-data-table__empty-wrapper) td:first-child {
	background: #f8fbff !important;
}
.co-dt ::v-deep .v-data-table__empty-wrapper td {
	height: auto;
}
.co-dt ::v-deep .v-data-footer {
	padding: 6px 16px;
	border-top: 1px solid var(--aq-border) !important;
	color: var(--aq-muted);
	font-size: 13px;
}
@media (max-width: 1360px) {
	.co-kpis {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
	.co-filters {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
	.co-filter--search {
		grid-column: 1 / -1;
	}
}
@media (max-width: 640px) {
	.co-kpis,
	.co-filters {
		grid-template-columns: minmax(0, 1fr);
	}
}
</style>
